/* Meeras Organics — site-wide cinematic scroll system.
   Drives two things off one rAF-throttled scroll loop:
   1. A continuous background color interpolation across the whole page
      via CSS custom properties on :root, so every .meeras-scope section
      inherits the same in-flight color — no per-section hard cuts.
      The palette stays entirely in the dark half of the brand's earthy
      range (charcoal / warm black / umber / deep olive / coffee): it
      never crosses into beige or cream. Text stays cream throughout,
      since the ground is always dark.

      The stop positions are anchored to where each real section's centre
      falls in page-scroll progress, and neighbouring stops alternate warm
      (brown / umber) against cool (olive / moss) so that no two adjacent
      sections ever settle on the same tone — while the interpolation
      between them keeps the change subconscious rather than a visible
      "jump". If sections are added or reordered in the theme editor the
      anchors below should be re-measured, since they are fractions of
      total page height rather than per-section observers.
   2. Continuous scroll-position-driven motion (blur / scale / drift /
      letter-spacing) on headings and the large background words, using
      each element's own distance from viewport-center rather than a
      one-shot IntersectionObserver trigger, so it tracks scroll speed
      and direction naturally.
   Kept dependency-free (no GSAP/ScrollTrigger) to match the rest of
   this theme's vanilla-JS sections. */
(function () {
  if (window.__meerasMotionInit) return;
  window.__meerasMotionInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  var STOPS = [
    { t: 0.000, c: [18, 16, 13] },  // page top — deep charcoal
    { t: 0.192, c: [20, 17, 14] },  // hero — near black (keeps the film clean)
    { t: 0.469, c: [31, 25, 19] },  // ingredients — warm black brown
    { t: 0.540, c: [32, 39, 29] },  // manifesto — deep olive charcoal
    { t: 0.582, c: [51, 36, 23] },  // story — dark earthy brown
    { t: 0.632, c: [26, 34, 28] },  // benefit — dark moss slate
    { t: 0.753, c: [61, 44, 29] },  // blend / stats — dark umber coffee
    { t: 0.970, c: [36, 27, 26] },  // cta — deep cocoa charcoal
    { t: 1.000, c: [36, 27, 26] }   // hold to the footer
  ];

  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function smoothstep(a, b, x) {
    var t = clamp01((x - a) / (b - a));
    return t * t * (3 - 2 * t);
  }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function lerpRGB(c1, c2, t) {
    return [Math.round(lerp(c1[0], c2[0], t)), Math.round(lerp(c1[1], c2[1], t)), Math.round(lerp(c1[2], c2[2], t))];
  }
  function colorAt(t) {
    for (var i = 0; i < STOPS.length - 1; i++) {
      var a = STOPS[i], b = STOPS[i + 1];
      if (t >= a.t && t <= b.t) return lerpRGB(a.c, b.c, smoothstep(a.t, b.t, t));
    }
    return t <= 0 ? STOPS[0].c.slice() : STOPS[STOPS.length - 1].c.slice();
  }
  function rgbStr(c) { return 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')'; }
  function rgbaStr(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'; }

  var motionEls = [];
  var bigwordEls = [];
  function collectEls() {
    motionEls = Array.prototype.slice.call(document.querySelectorAll('[data-meeras-motion="heading"]'));
    bigwordEls = Array.prototype.slice.call(document.querySelectorAll('[data-meeras-motion="bigword"]'));
  }

  function updateColors(t) {
    var bg = colorAt(t);
    root.style.setProperty('--meeras-bg', rgbStr(bg));
    root.style.setProperty('--meeras-header-bg', rgbaStr(bg, 0.82));
  }

  function updateMotion() {
    if (reduceMotion) return;
    var vh = window.innerHeight;
    for (var i = 0; i < motionEls.length; i++) {
      var el = motionEls[i];
      var r = el.getBoundingClientRect();
      var norm = ((r.top + r.height / 2) - vh / 2) / (vh * 0.62);
      if (norm > 1) norm = 1; else if (norm < -1) norm = -1;
      var abs = Math.abs(norm);
      var ty = norm * 30;
      var blur = Math.max(0, abs - 0.22) * 9;
      var opacity = clamp01(1 - Math.max(0, abs - 0.5) * 2.4);
      var scale = 1 - Math.max(0, abs - 0.3) * 0.045;
      var ls = Math.max(0, abs - 0.3) * 0.035;
      el.style.transform = 'translateY(' + ty.toFixed(1) + 'px) scale(' + scale.toFixed(3) + ')';
      el.style.filter = blur > 0.05 ? 'blur(' + blur.toFixed(2) + 'px)' : 'none';
      el.style.opacity = opacity.toFixed(3);
      el.style.letterSpacing = ls.toFixed(3) + 'em';
    }
    for (var j = 0; j < bigwordEls.length; j++) {
      var bw = bigwordEls[j];
      var factor = parseFloat(bw.getAttribute('data-speed') || '0.12');
      var br = bw.getBoundingClientRect();
      var bOffset = (br.top + br.height / 2) - vh / 2;
      bw.style.transform = 'translateY(' + (bOffset * -factor).toFixed(1) + 'px)';
    }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var t = max > 0 ? clamp01(window.scrollY / max) : 0;
      updateColors(t);
      updateMotion();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  function boot() {
    collectEls();
    onScroll();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
})();
